import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid,
  ListSubheader, MenuItem, TextField, Typography,
} from '@mui/material';
import { CloudUpload as UploadIcon, PlaceOutlined as PlaceIcon } from '@mui/icons-material';
import { projetDessinsAPI } from '../../services/api';
import { DessinOwnerType, PlanDestinataire, PlanFormMeta, PlanKind, ProjetDessin } from '../../types/projetDessin';
import { extractErrorMessage } from '../../utils/errorHandler';
import { isBinaryDxf } from '../../utils/dxfPreview';

const PROD_COLOR = '#FF5722';
/** Same names as the backend (plans_techniques.FAMILLES): the étape a plan creates by default */
/** Quantité / épaisseur: digits and one decimal comma or point only (the server reads « 2,5 » too) */
const nombre = (v: string) => /^\d*[.,]?\d*$/.test(v);
const FAMILLES: Record<string, string> = { decoupe: 'Découpe', pliage: 'Pliage', tournage: 'Tournage', usinage: 'Usinage', assemblage: 'Assemblage' };

export interface PlanOwner { type: DessinOwnerType; id: number; code: string; nom: string }

type Form = {
  kind: PlanKind; title: string; piece_ref: string; travail_id: number | ''; machine_id: number | ''; etape_id: number | '';
  matiere: string; epaisseur: string; quantite: string; observations: string;
};
const EMPTY: Form = { kind: 'pliage', title: '', piece_ref: '', travail_id: '', machine_id: '', etape_id: '', matiere: '', epaisseur: '', quantite: '', observations: '' };
const fromPlan = (p: ProjetDessin): Form => ({
  kind: p.kind, title: p.title, piece_ref: p.piece_ref || '', travail_id: p.travail_id || '', machine_id: p.machine_id || '',
  etape_id: p.etape_id || '', matiere: p.matiere || '', epaisseur: p.epaisseur != null ? String(Number(p.epaisseur)) : '',
  quantite: p.quantite != null ? String(Number(p.quantite)) : '', observations: p.observations || '',
});

/** Déposer / modifier un plan technique (Docs/PLANS_TECHNIQUES_PLAN.md). Without `plan`: a new plan
 *  for `owner`; with `plan`: its details, and a new version when a file is chosen. */
const PlanFormDialog: React.FC<{
  open: boolean; owner: PlanOwner | null; plan?: ProjetDessin | null; onClose: () => void; onSaved: (p: ProjetDessin) => void;
}> = ({ open, owner, plan, onClose, onSaved }) => {
  const edit = !!plan;
  const [meta, setMeta] = useState<PlanFormMeta | null>(null);
  const [f, setF] = useState<Form>(EMPTY);
  const [files, setFiles] = useState<File[]>([]);  // one plan, several files (the first is the main one)
  const file = files[0] || null;
  const [binaire, setBinaire] = useState(false);
  const [note, setNote] = useState('');
  const [dest, setDest] = useState<PlanDestinataire[] | null>(null);
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  // « Créer l'étape de fabrication »: the plan's what / machine / quantity become an étape to plan
  const [creerEtape, setCreerEtape] = useState(true);
  const [etapeNom, setEtapeNom] = useState('');
  const [etapeDuree, setEtapeDuree] = useState('60');

  useEffect(() => {
    if (!open || !owner) return;
    setF(plan ? fromPlan(plan) : EMPTY); setFiles([]); setBinaire(false); setNote(''); setErr(''); setMeta(null); setDest(null);
    setCreerEtape(true); setEtapeNom(''); setEtapeDuree('60');
    projetDessinsAPI.formMeta(owner.type, owner.id).then(setMeta).catch((e) => setErr(extractErrorMessage(e)));
  }, [open, owner, plan]);

  // live « Ce plan apparaîtra chez … »
  useEffect(() => {
    if (!open || !owner) return undefined;
    const t = setTimeout(() => {
      projetDessinsAPI.destinataires({ type: owner.type, id: owner.id, kind: f.kind, travail_id: f.travail_id || null,
        machine_id: f.machine_id || null, etape_id: f.etape_id || null }).then(setDest).catch(() => setDest(null));
    }, 300);
    return () => clearTimeout(t);
  }, [open, owner, f.kind, f.travail_id, f.machine_id, f.etape_id]);

  const set = <K extends keyof Form>(k: K, v: Form[K]) => setF((x) => ({ ...x, [k]: v }));
  const kindInfo = meta?.kinds.find((k) => k.value === f.kind);
  const machineRequise = !!meta?.machine_requise.includes(f.kind);
  const recommandees = useMemo(() => (meta?.machines || []).filter((m) => kindInfo?.famille && m.famille === kindInfo.famille), [meta, kindInfo]);
  const autres = useMemo(() => (meta?.machines || []).filter((m) => !recommandees.includes(m)), [meta, recommandees]);
  const machine = meta?.machines.find((m) => m.id === f.machine_id);
  const etapeDefaut = FAMILLES[machine?.famille || kindInfo?.famille || ''] || '';
  const offreEtape = !edit && !!meta?.comptoir && !f.etape_id;  // a new plan of a Comptoir job, no étape chosen
  const etapes = (meta?.etapes || []).filter((e) => !f.travail_id || !e.travail_id || e.travail_id === f.travail_id);

  const pickTravail = (id: number | '') => {
    const t = meta?.travaux.find((x) => x.id === id);
    setF((x) => ({
      ...x, travail_id: id,
      quantite: x.quantite || (t ? String(Number(t.quantite)) : ''),
      etape_id: x.etape_id && meta?.etapes.find((e) => e.id === x.etape_id && e.travail_id && e.travail_id !== id) ? '' : x.etape_id,
    }));
  };
  const pickEtape = (id: number | '') => {
    const e = meta?.etapes.find((x) => x.id === id);
    setF((x) => ({ ...x, etape_id: id, machine_id: x.machine_id || e?.machine_id || '', travail_id: x.travail_id || e?.travail_id || '' }));
  };
  const pickFiles = async (list: File[]) => {
    setFiles(list);
    let bin = false;
    for (const x of list) {
      if (x.name.toLowerCase().endsWith('.dxf') && isBinaryDxf(new Uint8Array(await x.slice(0, 22).arrayBuffer()))) bin = true;
    }
    setBinaire(bin);
  };

  const save = async () => {
    if (!owner) return;
    if (!f.title.trim()) { setErr('Indiquez la pièce (désignation).'); return; }
    if (machineRequise && !f.machine_id) { setErr("Choisissez la machine : c'est elle qui envoie le plan au bon opérateur."); return; }
    if (!edit && !file) { setErr('Choisissez le fichier du plan.'); return; }
    if (files.length && f.kind === 'dxf' && !files.some((x) => x.name.toLowerCase().endsWith('.dxf'))) { setErr('Un plan DXF doit contenir un fichier .dxf.'); return; }
    if (edit && file && !note.trim()) { setErr('Indiquez ce qui change dans cette version (ex : cote 120 → 125 mm).'); return; }
    const avecEtape = offreEtape && creerEtape;
    if (avecEtape && !(etapeNom.trim() || etapeDefaut)) { setErr("Indiquez l'étape à créer (ex : Découpe, Pliage)."); return; }
    if (avecEtape && !(Number(etapeDuree) > 0)) { setErr("Indiquez la durée estimée de l'étape en minutes."); return; }
    const fields = {
      kind: f.kind, piece_ref: f.piece_ref.trim() || null, travail_id: f.travail_id || null, machine_id: f.machine_id || null,
      etape_id: f.etape_id || null, matiere: f.matiere.trim() || null, epaisseur: f.epaisseur || null, quantite: f.quantite || null,
      observations: f.observations.trim() || null,
      ...(offreEtape && creerEtape
        ? { creer_etape: true, etape_designation: etapeNom.trim() || etapeDefaut, etape_duree_min: Number(etapeDuree) }
        : {}),
    };
    setSaving(true); setErr('');
    try {
      if (!edit) {
        onSaved(await projetDessinsAPI.create(owner.type, owner.id, files, f.title.trim(), undefined, note.trim() || undefined, undefined, fields));
      } else {
        let saved = await projetDessinsAPI.update(plan!.id, { title: f.title.trim(), ...fields });
        if (file) {
          await projetDessinsAPI.uploadVersion(plan!.id, files, note.trim());
          saved = (await projetDessinsAPI.listFor(owner.type, owner.id)).items.find((x) => x.id === plan!.id) || saved;
        }
        onSaved(saved);
      }
    } catch (e: any) { setErr(extractErrorMessage(e)); } finally { setSaving(false); }
  };

  const nextV = (plan?.latest_version?.version || 0) + 1;
  return (
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="md" fullWidth>
      <DialogTitle>{edit ? `Modifier le plan · ${plan!.title}` : 'Déposer un plan technique'}{owner ? ` · ${owner.code}` : ''}</DialogTitle>
      <DialogContent dividers>
        {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
        {!meta && !err ? <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box> : meta && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField select label="Type de plan" required fullWidth value={f.kind} onChange={(e) => set('kind', e.target.value as PlanKind)}>
                {meta.kinds.filter((k) => meta.machine_requise.includes(k.value)).map((k) => <MenuItem key={k.value} value={k.value}>{k.label}</MenuItem>)}
                <ListSubheader>Autres types</ListSubheader>
                {meta.kinds.filter((k) => !meta.machine_requise.includes(k.value)).map((k) => <MenuItem key={k.value} value={k.value}>{k.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={8} sm={4}>
              <TextField label="Pièce (désignation)" required fullWidth value={f.title} onChange={(e) => set('title', e.target.value)}
                placeholder="ex : Flasque latérale" inputProps={{ maxLength: 200 }} />
            </Grid>
            <Grid item xs={4} sm={2}>
              <TextField label="Référence" fullWidth value={f.piece_ref} onChange={(e) => set('piece_ref', e.target.value)} placeholder="P-01" inputProps={{ maxLength: 50 }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select label="Travail concerné" fullWidth value={f.travail_id} onChange={(e) => pickTravail(e.target.value === '' ? '' : Number(e.target.value))}
                SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}
                helperText={meta.comptoir ? undefined : 'Projet hors Comptoir : le plan vaut pour tout le projet'} disabled={!meta.travaux.length}>
                <MenuItem value="">Tout le projet</MenuItem>
                {meta.travaux.map((t) => <MenuItem key={t.id} value={t.id}>{t.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select label="Machine" required={machineRequise} fullWidth value={f.machine_id}
                onChange={(e) => set('machine_id', e.target.value === '' ? '' : Number(e.target.value))}
                SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}>
                <MenuItem value="">{machineRequise ? '— Choisir la machine —' : 'Aucune machine précise'}</MenuItem>
                {recommandees.length > 0 && <ListSubheader>Recommandées · {kindInfo?.short}</ListSubheader>}
                {recommandees.map((m) => <MenuItem key={m.id} value={m.id}>{m.code} — {m.name}</MenuItem>)}
                {recommandees.length > 0 && <ListSubheader>Autres machines</ListSubheader>}
                {autres.map((m) => <MenuItem key={m.id} value={m.id}>{m.code} — {m.name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select label="Étape de fabrication" fullWidth value={f.etape_id} onChange={(e) => pickEtape(e.target.value === '' ? '' : Number(e.target.value))}
                SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }} disabled={!meta.etapes.length}
                helperText={meta.etapes.length ? 'Facultatif : sinon, toutes les étapes sur cette machine' : 'Aucune étape Comptoir pour ce travail'}>
                <MenuItem value="">Toutes (selon la machine)</MenuItem>
                {etapes.map((e) => <MenuItem key={e.id} value={e.id}>{e.label}{e.machine ? ` · ${e.machine}` : ''}{e.responsable ? ` · ${e.responsable}` : ''}</MenuItem>)}
              </TextField>
            </Grid>
            {offreEtape && (
              <Grid item xs={12}>
                <Box sx={{ p: 1.5, borderRadius: 2, border: 1, borderColor: creerEtape ? PROD_COLOR : 'divider' }}>
                  <FormControlLabel control={<Checkbox checked={creerEtape} onChange={(e) => setCreerEtape(e.target.checked)} />}
                    label={<><b>Créer l'étape de fabrication</b> à partir de ce plan (machine, travail, quantité) — à planifier ensuite</>} />
                  {creerEtape && (
                    <Grid container spacing={2} sx={{ mt: 0 }}>
                      <Grid item xs={7}>
                        <TextField label="Étape" fullWidth size="small" value={etapeNom} onChange={(e) => setEtapeNom(e.target.value)}
                          placeholder={etapeDefaut || 'ex : Pliage'} InputLabelProps={{ shrink: true }}
                          helperText={etapeDefaut && !etapeNom ? `Par défaut : ${etapeDefaut}` : ' '} inputProps={{ maxLength: 100 }} />
                      </Grid>
                      <Grid item xs={5}>
                        <TextField label="Durée estimée (min)" required fullWidth size="small" value={etapeDuree}
                          onChange={(e) => setEtapeDuree(e.target.value.replace(/\D/g, ''))} inputProps={{ inputMode: 'numeric' }}
                          helperText="Le planning choisit ensuite le jour, l'heure et l'opérateur" />
                      </Grid>
                    </Grid>
                  )}
                </Box>
              </Grid>
            )}
            <Grid item xs={12} sm={4}>
              <TextField label="Quantité" fullWidth value={f.quantite} onChange={(e) => nombre(e.target.value) && set('quantite', e.target.value)} inputProps={{ inputMode: 'decimal' }} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label="Épaisseur (mm)" fullWidth value={f.epaisseur} onChange={(e) => nombre(e.target.value) && set('epaisseur', e.target.value)} inputProps={{ inputMode: 'decimal' }} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label="Matière" fullWidth value={f.matiere} onChange={(e) => set('matiere', e.target.value)} placeholder="Inox 304" />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Observations techniques" fullWidth multiline minRows={2} value={f.observations} onChange={(e) => set('observations', e.target.value)}
                placeholder="Cotes critiques, sens de pliage, tolérances…" helperText="Affichées à l'opérateur avec le plan" />
            </Grid>
            <Grid item xs={12}>
              <Box onClick={() => document.getElementById('plan-file-input')?.click()}
                sx={{ p: 2, border: '2px dashed', borderColor: 'divider', borderRadius: 2, textAlign: 'center', cursor: 'pointer',
                  '&:hover': { borderColor: PROD_COLOR } }}>
                <input id="plan-file-input" type="file" hidden multiple
                  onChange={(e) => pickFiles(Array.from(e.target.files || []))} />
                <UploadIcon sx={{ color: PROD_COLOR }} />
                <Typography variant="body2" sx={{ fontWeight: file ? 700 : 400 }}>
                  {files.length > 1 ? `${files.length} fichiers : ${files.map((x) => x.name).join(', ')}` : file ? file.name
                    : edit ? `Nouveau(x) fichier(s) → crée la version v${nextV} (facultatif)` : 'Choisir le(s) fichier(s) du plan'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {f.kind === 'dxf' ? 'Au moins un fichier .dxf (DXF ASCII pour l\'aperçu) + d\'autres fichiers si besoin' : 'Un ou plusieurs fichiers : PDF, image, DXF… (max 100 MB chacun)'}
                </Typography>
              </Box>
              {binaire && <Alert severity="warning" sx={{ mt: 1 }}>Ce DXF est enregistré en binaire : il sera gardé, mais sans aperçu à l'écran. Pour l'aperçu, enregistrez-le en « DXF ASCII ».</Alert>}
            </Grid>
            {(file || !edit) && (
              <Grid item xs={12}>
                <TextField label={edit ? `Ce qui change dans la v${nextV}` : 'Note de version (facultative)'} required={edit} fullWidth value={note}
                  onChange={(e) => setNote(e.target.value)} placeholder={edit ? 'ex : cote 120 → 125 mm' : undefined} />
              </Grid>
            )}
          </Grid>
        )}
        {dest && (
          <Alert severity={dest.length ? 'info' : 'warning'} icon={<PlaceIcon />} sx={{ mt: 2 }}>
            {dest.length
              ? <>Ce plan apparaîtra chez : <b>{dest.map((d) => `${d.label}${d.machine ? ` · ${d.machine}` : ''} · ${d.responsable || 'à désigner'}`).join(' ; ')}</b></>
              : "Aucune tâche ne correspond pour l'instant : il apparaîtra dès qu'une étape ou une tâche sera planifiée sur cette machine."}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Annuler</Button>
        <Button variant="contained" onClick={save} disabled={saving || !meta} startIcon={saving ? <CircularProgress size={16} /> : <UploadIcon />}
          sx={{ bgcolor: PROD_COLOR, '&:hover': { bgcolor: '#E64A19' } }}>
          {edit ? (file ? `Enregistrer la v${nextV}` : 'Enregistrer') : 'Déposer le plan'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PlanFormDialog;
